import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList as List } from 'react-window'
import { v4 as uuid_v4 } from 'uuid'
import { Button, Card, CardContent, Stack, Typography } from '@mui/material'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import { type IBook } from '../../models/book/book-types'
import Loader from '../../components/loader/loader'
import BookCard from '../../components/card/book/book-card'

interface BookCardListProps {
  bookUrl: string | null
}

interface Message {
  channelId: string
  data: IBook[]
}

const BookCardList: React.FC<BookCardListProps> = ({ bookUrl }) => {
  const [books, setBooks] = useState<IBook[]>([])

  const { isAuthorized, accessToken } = useContext(AuthContext)
  useEffect(() => {
    if (isAuthorized) {
      console.debug('[Books] Ready to connect')
      setShouldConnect(true)
    }
  }, [isAuthorized])

  useEffect(() => {
    if (bookUrl == null) {
      console.debug('[Books] Skipped connecting to data stream (reason: null url)')
      setAttemptedApiConnect(true)
    }
  }, [bookUrl])

  const [attemptedApiConnect, setAttemptedApiConnect] = useState<boolean>(false)
  const [reconnectAttemptNo, setReconnectAttemptNo] = useState<number>(0)
  const [maxAttemptedReconnects, setMaxAttemptedReconnects] = useState<boolean>(false)
  const [shouldConnect, setShouldConnect] = useState<boolean>(false)

  const { lastJsonMessage, readyState }: { lastJsonMessage: Message | null, readyState: number } = useWebSocket(
    bookUrl,
    {
      share: true,
      protocols: ['Authorization', accessToken ?? 'no-token'],
      onOpen: () => {
        console.log('[Books] WebSocket connection established')
        setAttemptedApiConnect(true)
        setReconnectAttemptNo(0)
      },
      onClose: (event) => {
        console.log('[Books] WebSocket connection closed', event)
        setAttemptedApiConnect(true)
      },
      onError: (error) => {
        console.error('[Books] Error encountered when processing message: ', error)
      },
      onReconnectStop: () => {
        console.error('[Books] Max reconnection attempts reached')
        setMaxAttemptedReconnects(true)
        setShouldConnect(false)
      },
      filter: (message) => {
        const parsedData = JSON.parse(message.data)
        return parsedData?.channelId === 'books'
      },
      shouldReconnect: () => {
        const newAttemptCount = reconnectAttemptNo + 1
        setReconnectAttemptNo(newAttemptCount)
        return true
      },
      retryOnError: true,
    },
    shouldConnect,
  )

  useEffect(() => {
    if (lastJsonMessage == null) {
      setBooks([])
    } else {
      setBooks(lastJsonMessage.data)
    }
  }, [lastJsonMessage])

  return (
    <Card
      variant='outlined'
      sx={{
        width: 1,
        height: '100%',
        padding: 1,
        background: 'transparent',
        borderRadius: 0,
      }}
    >
      <CardContent
        sx={{
          height: 'calc(100% - 24px)',
          padding: 0,
          margin: 0,
        }}
      >
        <Typography variant='h6' fontWeight='bold' marginBottom={1}>
          Books
        </Typography>

        {
          readyState === ReadyState.OPEN
            ? <AutoSizer>
              {
                ({ height, width }) =>
                  (
                    <List
                      width={width}
                      height={height}
                      itemSize={86}
                      itemData={books}
                      itemCount={books.length}
                    >
                      {
                        ({ data, index, style }) =>
                          <BookCard
                            style={style}
                            data={data[index]}
                            key={uuid_v4()}
                          />
                      }
                    </List>
                  )
              }
            </AutoSizer>
            : <Stack alignItems='center' justifyContent='center' gap={1}>
              {
                attemptedApiConnect
                  ? <PowerOffIcon color='error' fontSize='large'/>
                  : <Loader/>
              }

              {
                (!maxAttemptedReconnects && reconnectAttemptNo > 0)
                  ? <Typography color='primary' align='center'>
                    {`Attempted reconnection ${reconnectAttemptNo} time${reconnectAttemptNo > 1 ? 's' : ''}`}
                  </Typography>
                  : null
              }

              {
                maxAttemptedReconnects
                  ? <>
                    <Typography color='primary' align='center'>
                      Max auto-reconnection attempts reached
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => {
                        setReconnectAttemptNo(0)
                        setMaxAttemptedReconnects(false)
                        setShouldConnect(true)
                      }}
                    >
                      Reconnect
                    </Button>
                  </>
                  : null
              }
            </Stack>
        }
      </CardContent>
    </Card>
  )
}

export default BookCardList
